/* eslint-disable no-restricted-imports */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { DpProductCard } from '@/components/blocks/DpLibrary/DpProductCard';
import { useProductEvents } from '@/data/Content/_ProductEvents';
import { ProductType } from '@/data/types/Product';
import { formatPrice } from 'integration/utils/formatPrice';
import { getImage } from '@/utils/getImage';
import { Grid } from '@mui/material';
import { calculateProductPrices } from 'integration/utils/calculateProductPrices';
import { findProductAttribute } from 'integration/utils/findProductAttribute';
import { FC, useMemo, useState } from 'react';
import { useProductExtractMerchandisingAssociations } from 'integration/hooks/useProductExtractMerchandisingAssociations';
import { useProductsWithInfoElectronico } from '@/data/Content/_Products_InfoElectronico';
import { orderColorVariantsByBase } from '@/utils/productColor';

interface InfoElectronico {
	offerDeContado?: string;
	strikedDeContado?: string;
}

export const ProductCard: FC<{
	product: ProductType;
	infoElectronico?: InfoElectronico;
	clickAction?: () => void;
}> = ({ product: _product, clickAction, infoElectronico }) => {
	const { productVariantColors } = useProductExtractMerchandisingAssociations(_product);
	const orderedVariantColors = useMemo(
		() => orderColorVariantsByBase(productVariantColors, _product.partNumber),
		[productVariantColors, _product.partNumber]
	);
	const [product, setProduct] = useState(_product);
	const { onClick } = useProductEvents({ product });

	const { data: productsWithInfoElectronico } = useProductsWithInfoElectronico([product]);

	const productWithInfoElectronico = useMemo(() => {
		if (productsWithInfoElectronico && productsWithInfoElectronico.length > 0) {
			return productsWithInfoElectronico[0];
		}
		return _product;
	}, [productsWithInfoElectronico, _product]);

	const { price, priceWithoutOffer, shouldOffer } = calculateProductPrices({
		list: productWithInfoElectronico.productPrice.list || 0,
		offer: productWithInfoElectronico.productPrice.offer || 0,
		ribbons: productWithInfoElectronico?.ribbons ?? [],
	});

	const category = useMemo(() => {
		const family = findProductAttribute(productWithInfoElectronico.attributes, 'Familia');
		const gender = findProductAttribute(productWithInfoElectronico.attributes, 'Genero');
		const uso = findProductAttribute(productWithInfoElectronico.attributes, 'Uso');
		const brand = findProductAttribute(productWithInfoElectronico.attributes, 'Marca');

		if (uso?.value === 'Electronicos' && brand) {
			return `${brand.value}`;
		}

		if (family && gender) {
			return `${family.value} ${gender.value}`;
		}

		return productWithInfoElectronico.manufacturer;
	}, [productWithInfoElectronico.attributes, productWithInfoElectronico.manufacturer]);

	const prices = useMemo(() => {
		if (infoElectronico) {
			return {
				discount: infoElectronico.strikedDeContado ? infoElectronico.offerDeContado : undefined,
				price: infoElectronico.strikedDeContado || infoElectronico.offerDeContado || '',
			};
		}

		return {
			price: priceWithoutOffer,
			discount: shouldOffer ? price : undefined,
		};
	}, [infoElectronico, price, priceWithoutOffer, shouldOffer]);

	return (
		<Grid
			onClick={onClick(clickAction)}
			id={productWithInfoElectronico.partNumber}
			data-testid={productWithInfoElectronico.partNumber}
		>
			<DpProductCard
				seo={product?.seo?.href}
				title={productWithInfoElectronico.name}
				category={Array.isArray(category) ? category[0] : category ?? ''}
				image={getImage(productWithInfoElectronico.thumbnail)}
				price={formatPrice(prices.price, 'mxn')}
				discount={prices.discount ? formatPrice(prices.discount, 'mxn') : undefined}
				ribbons={product?.ribbons ?? []}
				showPrice={productWithInfoElectronico.showPrice}
				variants={orderedVariantColors}
				partNumber={productWithInfoElectronico.partNumber}
				onSelectColor={(variant) => setProduct(variant)}
			/>
		</Grid>
	);
};
